import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Crop,
  Sparkles,
  RotateCw,
  RefreshCw,
  Check,
  X,
  Maximize2,
  ZoomIn,
  Move,
  Lock,
  Unlock,
} from "lucide-react";
import {
  createCanvas,
  perspectiveWarp,
  detectDocumentCorners,
  rotateCanvas,
  A4_PAGE_PHYSICAL,
  ID_CARD_PHYSICAL,
} from "@/lib/imageEnhancer";

export default function DocumentCropEditor({
  sourceCanvas,
  onConfirm,
  onCancel,
  targetMode = "document", // 'document' | 'id_card'
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // Active working source canvas (can be rotated)
  const [currentSource, setCurrentSource] = useState(sourceCanvas);

  // Corners in natural image coordinates: [TL, TR, BR, BL]
  const [corners, setCorners] = useState(null);

  // Dragging state
  const [activeHandleIndex, setActiveHandleIndex] = useState(null);
  const [isDraggingAll, setIsDraggingAll] = useState(false);
  const dragStartRef = useRef(null);

  // Aspect ratio lock: 'free' | 'a4' | 'id_card'
  const [aspectLock, setAspectLock] = useState(targetMode === "id_card" ? "id_card" : "free");

  // Display scale factor (displayPx / naturalPx)
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Initialize corners on load or source change
  useEffect(() => {
    if (currentSource) {
      const detected = detectDocumentCorners(currentSource);
      setCorners(detected);
    }
  }, [currentSource]);

  // Recalculate display dimensions on resize
  const updateLayout = useCallback(() => {
    if (!containerRef.current || !currentSource) return;
    const container = containerRef.current;
    const cw = container.clientWidth;
    const ch = container.clientHeight;

    const imgW = currentSource.width;
    const imgH = currentSource.height;

    const scaleFactor = Math.min((cw - 40) / imgW, (ch - 40) / imgH, 1);
    const dispW = Math.round(imgW * scaleFactor);
    const dispH = Math.round(imgH * scaleFactor);

    const offX = Math.round((cw - dispW) / 2);
    const offY = Math.round((ch - dispH) / 2);

    setScale(scaleFactor);
    setOffset({ x: offX, y: offY });
  }, [currentSource]);

  useEffect(() => {
    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [updateLayout]);

  // Convert client/touch coordinates to image coordinates
  const clientToImageCoords = (clientX, clientY) => {
    if (!containerRef.current || !currentSource) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const xInContainer = clientX - rect.left - offset.x;
    const yInContainer = clientY - rect.top - offset.y;

    const imgX = Math.round(xInContainer / scale);
    const imgY = Math.round(yInContainer / scale);

    return {
      x: Math.max(0, Math.min(currentSource.width, imgX)),
      y: Math.max(0, Math.min(currentSource.height, imgY)),
    };
  };

  // -------------------------------------------------------------
  // Pointer / Touch Handlers
  // -------------------------------------------------------------
  const handlePointerDown = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveHandleIndex(index);
  };

  const handlePointerMove = useCallback(
    (e) => {
      if (activeHandleIndex === null || !corners || !currentSource) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      const { x, y } = clientToImageCoords(clientX, clientY);

      setCorners((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        next[activeHandleIndex] = { x, y };
        return next;
      });
    },
    [activeHandleIndex, corners, currentSource, scale, offset]
  );

  const handlePointerUp = useCallback(() => {
    setActiveHandleIndex(null);
  }, []);

  useEffect(() => {
    if (activeHandleIndex !== null) {
      window.addEventListener("mousemove", handlePointerMove, { passive: false });
      window.addEventListener("mouseup", handlePointerUp);
      window.addEventListener("touchmove", handlePointerMove, { passive: false });
      window.addEventListener("touchend", handlePointerUp);

      return () => {
        window.removeEventListener("mousemove", handlePointerMove);
        window.removeEventListener("mouseup", handlePointerUp);
        window.removeEventListener("touchmove", handlePointerMove);
        window.removeEventListener("touchend", handlePointerUp);
      };
    }
  }, [activeHandleIndex, handlePointerMove, handlePointerUp]);

  // -------------------------------------------------------------
  // Action Helpers
  // -------------------------------------------------------------
  const handleAutoDetect = () => {
    if (!currentSource) return;
    const detected = detectDocumentCorners(currentSource);
    setCorners(detected);
  };

  const handleRotate = () => {
    if (!currentSource) return;
    const rotated = rotateCanvas(currentSource, 90);
    setCurrentSource(rotated);
  };

  const handleReset = () => {
    if (!currentSource) return;
    const w = currentSource.width;
    const h = currentSource.height;
    setCorners([
      { x: Math.round(w * 0.04), y: Math.round(h * 0.04) },
      { x: Math.round(w * 0.96), y: Math.round(h * 0.04) },
      { x: Math.round(w * 0.96), y: Math.round(h * 0.96) },
      { x: Math.round(w * 0.04), y: Math.round(h * 0.96) },
    ]);
  };

  const handleConfirmCrop = () => {
    if (!currentSource || !corners) return;

    let lockedRatio = null;
    if (aspectLock === "a4") lockedRatio = A4_PAGE_PHYSICAL.aspectRatio;
    if (aspectLock === "id_card") lockedRatio = ID_CARD_PHYSICAL.aspectRatio;

    const warped = perspectiveWarp(currentSource, corners, null, null, { lockedRatio });
    onConfirm({
      croppedCanvas: warped,
      corners,
      sourceCanvas: currentSource,
    });
  };

  const handleLabels = ["Top-Left", "Top-Right", "Bottom-Right", "Bottom-Left"];
  const handleColors = ["#f59e0b", "#38bdf8", "#10b981", "#a855f7"];

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/95 backdrop-blur-md flex flex-col justify-between overflow-hidden select-none animate-fade-in">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/90 border-b border-zinc-800 shadow-md">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold">
            <Crop className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-black text-white tracking-tight flex items-center gap-1.5">
              <span>Crop & Perspective Deskew</span>
            </h2>
            <p className="text-[11px] text-zinc-400">Drag 4 corners to document edges</p>
          </div>
        </div>

        {/* Aspect Ratio Selector */}
        <div className="hidden sm:flex items-center bg-zinc-950 border border-zinc-800 rounded-xl p-0.5 text-xs">
          {[
            { id: "free", label: "Free Ratio" },
            { id: "a4", label: "A4 Document" },
            { id: "id_card", label: "CR80 ID Card" },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setAspectLock(item.id)}
              className={`px-2.5 py-1 rounded-lg font-bold transition-all text-[11px] ${
                aspectLock === item.id
                  ? "bg-amber-500 text-zinc-950 shadow-sm"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Close / Cancel */}
        <button
          type="button"
          onClick={onCancel}
          className="p-2 rounded-xl bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Main Interactive Crop Area */}
      <div
        ref={containerRef}
        className="relative flex-1 w-full h-full flex items-center justify-center overflow-hidden touch-none"
      >
        {currentSource && corners && (
          <div
            className="relative shadow-2xl"
            style={{
              width: `${currentSource.width * scale}px`,
              height: `${currentSource.height * scale}px`,
            }}
          >
            {/* Base Image */}
            <img
              src={currentSource.toDataURL()}
              alt="Source Scan"
              className="w-full h-full object-contain pointer-events-none rounded-lg"
            />

            {/* SVG Quad Overlay with Mask & Grid */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <defs>
                <mask id="crop-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <polygon
                    points={corners.map((c) => `${c.x * scale},${c.y * scale}`).join(" ")}
                    fill="black"
                  />
                </mask>
              </defs>

              {/* Darkened Mask outside crop quad */}
              <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.65)" mask="url(#crop-mask)" />

              {/* Quad Boundary */}
              <polygon
                points={corners.map((c) => `${c.x * scale},${c.y * scale}`).join(" ")}
                fill="rgba(245, 158, 11, 0.12)"
                stroke="#f59e0b"
                strokeWidth="2.5"
                strokeDasharray="6 4"
              />

              {/* Internal 3x3 Grid Lines */}
              {[1, 2].map((i) => {
                const frac = i / 3;
                const p0 = corners[0];
                const p1 = corners[1];
                const p2 = corners[2];
                const p3 = corners[3];

                // Horizontal line
                const h1x = (p0.x + frac * (p3.x - p0.x)) * scale;
                const h1y = (p0.y + frac * (p3.y - p0.y)) * scale;
                const h2x = (p1.x + frac * (p2.x - p1.x)) * scale;
                const h2y = (p1.y + frac * (p2.y - p1.y)) * scale;

                // Vertical line
                const v1x = (p0.x + frac * (p1.x - p0.x)) * scale;
                const v1y = (p0.y + frac * (p1.y - p0.y)) * scale;
                const v2x = (p3.x + frac * (p2.x - p3.x)) * scale;
                const v2y = (p3.y + frac * (p2.y - p3.y)) * scale;

                return (
                  <g key={i}>
                    <line x1={h1x} y1={h1y} x2={h2x} y2={h2y} stroke="rgba(245, 158, 11, 0.35)" strokeWidth="1" />
                    <line x1={v1x} y1={v1y} x2={v2x} y2={v2y} stroke="rgba(245, 158, 11, 0.35)" strokeWidth="1" />
                  </g>
                );
              })}
            </svg>

            {/* 4 Draggable Handles */}
            {corners.map((c, idx) => {
              const cx = c.x * scale;
              const cy = c.y * scale;
              const isActive = activeHandleIndex === idx;

              return (
                <div
                  key={idx}
                  onMouseDown={(e) => handlePointerDown(idx, e)}
                  onTouchStart={(e) => handlePointerDown(idx, e)}
                  style={{
                    left: `${cx}px`,
                    top: `${cy}px`,
                  }}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-crosshair touch-manipulation z-30 transition-transform ${
                    isActive ? "scale-125" : "hover:scale-110"
                  }`}
                >
                  {/* Outer Glow Circle */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shadow-2xl transition-all"
                    style={{
                      backgroundColor: handleColors[idx],
                      boxShadow: `0 0 16px ${handleColors[idx]}88`,
                    }}
                  >
                    <div className="w-3.5 h-3.5 bg-zinc-950 rounded-full border-2 border-white" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Controls Toolbar */}
      <div className="bg-zinc-900/95 border-t border-zinc-800 px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-2xl">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleAutoDetect}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-amber-400 transition-all active:scale-95 shadow-sm"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>Auto Detect</span>
          </button>

          <button
            type="button"
            onClick={handleRotate}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-300 hover:text-white transition-all active:scale-95 shadow-sm"
          >
            <RotateCw className="w-3.5 h-3.5" />
            <span>Rotate 90°</span>
          </button>

          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-300 hover:text-white transition-all active:scale-95 shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        </div>

        {/* Confirm Done Button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs hover:bg-zinc-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmCrop}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 active:scale-95 text-zinc-950 font-black text-xs shadow-lg shadow-amber-500/25 transition-all cursor-pointer"
          >
            <Check className="w-4 h-4" />
            <span>Done & Enhance</span>
          </button>
        </div>
      </div>
    </div>
  );
}
